import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowLeft, Calendar as CalIcon, MapPin, Clock, Loader2, Plus, CheckSquare, CalendarClock, X, Trash2, AlignLeft, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { jobsApi, calendarApi, tasksApi } from "../lib/api";
import MobileCreateModal from "../components/mobile/MobileCreateModal";
import CommandThemeScope from "../components/CommandThemeScope";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";
import "../theme-mobile.css";

const MS_DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const startOfMonth = (d) => { const x = startOfDay(d); x.setDate(1); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const fmtMonthYear = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const fmtHour = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const STATUS_COLORS = {
  "Unassigned": "#94a3b8",
  "Scheduled": "#3b82f6",
  "Dispatched": "#8b5cf6",
  "En Route": "#f59e0b",
  "On Site": "#10b981",
  "In Progress": "#22c55e",
  "Completed": "#059669",
  "Canceled": "#ef4444",
};

// Six-week grid the desktop calendar uses — same shape but mobile-sized cells.
function monthGrid(anchor) {
  const first = startOfMonth(anchor);
  const startDow = first.getDay();
  const start = addDays(first, -startDow);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

// Fetch 42-day range around a month anchor. Kept as a top-level helper so the
// initial useEffect can call it in parallel with `jobsApi.list()`.
async function _safeEvents(anchor) {
  try {
    const first = startOfMonth(anchor);
    const startIso = addDays(first, -first.getDay()).toISOString();
    const endIso = addDays(first, 42).toISOString();
    return await calendarApi.listEvents({ start: startIso, end: endIso });
  } catch {
    return [];
  }
}

export default function MobileCalendar() {
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  const today = startOfDay(new Date());
  const [anchor, setAnchor] = useState(today);
  const [selected, setSelected] = useState(today);
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]); // calendar events + task-derived virtual events
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null); // clicked item (job/event/task)

  // Fetch events (which the backend enriches with task-derived virtual events)
  // spanning the visible 42-day month grid.
  const fetchEvents = () => {
    const first = startOfMonth(anchor);
    const startIso = addDays(first, -first.getDay()).toISOString();
    const endIso = addDays(first, 42).toISOString();
    return calendarApi.listEvents({ start: startIso, end: endIso })
      .then((rows) => setEvents(rows || []))
      .catch(() => setEvents([]));
  };

  const fetchJobs = () => {
    return jobsApi.list()
      .then((rows) => setJobs(rows || []))
      .catch(() => setJobs([]));
  };

  const refreshAll = () => Promise.all([fetchJobs(), fetchEvents()]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([jobsApi.list(), _safeEvents(anchor)])
      .then(([js, evs]) => {
        if (cancelled) return;
        setJobs(js || []);
        setEvents(evs || []);
      })
      .catch(() => { if (!cancelled) { setJobs([]); setEvents([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Re-fetch events when the user pages through months so the dots + agenda
  // stay in sync with what's actually scheduled.
  useEffect(() => {
    fetchEvents();
  }, [anchor]);

  // Bucket every item (job, event, or task) by its local YYYY-MM-DD start date.
  const itemsByDate = useMemo(() => {
    const out = {};
    const bucket = (iso, item) => {
      if (!iso) return;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return;
      const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (out[k] ||= []).push(item);
    };
    jobs.forEach((j) => bucket(j.start, { ...j, kind: "job" }));
    events.forEach((e) => bucket(e.start, { ...e, kind: e.source === "task" ? "task" : "event" }));
    Object.keys(out).forEach((k) => out[k].sort((a, b) => (a.start || "").localeCompare(b.start || "")));
    return out;
  }, [jobs, events]);

  const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const selectedItems = itemsByDate[dayKey(selected)] || [];
  const grid = useMemo(() => monthGrid(anchor), [anchor]);

  return (
    <div className="cc-mobile cc-glow relative w-full min-h-screen overflow-x-hidden flex flex-col" data-testid="mobile-calendar-page">
      <CommandThemeScope surface="mobile" />

      {/* App bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-3 h-14 bg-white border-b border-slate-200">
        <button
          onClick={() => goBack("/dashboard")}
          data-testid="mobile-cal-back"
          className="h-10 w-10 flex items-center justify-center rounded-full bg-slate-100 active:bg-slate-200 text-slate-700"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <div className="text-[16px] font-black tracking-tight text-[#0B1E3F] inline-flex items-center gap-2">
          <CalIcon className="h-5 w-5 text-blue-600" /> Calendar
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          data-testid="mobile-cal-create-btn"
          aria-label="Create event or task"
          className="h-10 w-10 flex items-center justify-center rounded-full bg-blue-600 text-white active:bg-blue-700 shadow-sm shadow-blue-900/20"
        >
          <Plus className="h-5 w-5" strokeWidth={2.5} />
        </button>
      </header>

      {/* Month header */}
      <div className="px-4 pt-3 flex items-center justify-between" data-testid="mobile-cal-month-nav">
        <button
          onClick={() => setAnchor(addMonths(anchor, -1))}
          data-testid="mobile-cal-prev"
          className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5 shrink-0" />
        </button>
        <div className="text-[16px] font-bold text-[#0B1E3F]">{fmtMonthYear(anchor)}</div>
        <button
          onClick={() => setAnchor(addMonths(anchor, 1))}
          data-testid="mobile-cal-next"
          className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5 shrink-0" />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 px-3 mt-2 text-center text-[10.5px] font-bold uppercase tracking-wider text-slate-500">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 gap-1 px-3 mt-1" data-testid="mobile-cal-grid">
        {grid.map((d) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          const isSelected = sameDay(d, selected);
          const dayItems = itemsByDate[dayKey(d)] || [];
          // Up to 3 dots. Jobs use their status color; events are blue; tasks amber.
          const dots = dayItems.slice(0, 3).map((it) => {
            if (it.kind === "job") return STATUS_COLORS[it.status] || "#94a3b8";
            if (it.kind === "task") return "#f59e0b";
            return it.color || "#3b82f6";
          });
          return (
            <button
              key={dayKey(d)}
              onClick={() => setSelected(d)}
              data-testid={`mobile-cal-day-${dayKey(d)}`}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-[13px] font-semibold transition-colors ${
                isSelected
                  ? "bg-blue-600 text-white"
                  : isToday
                    ? "bg-blue-50 text-blue-700 border border-blue-300"
                    : inMonth
                      ? "bg-white text-slate-800 border border-slate-100"
                      : "text-slate-400"
              }`}
            >
              <div>{d.getDate()}</div>
              {dayItems.length > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {dots.map((color, i) => (
                    <span
                      key={i}
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: isSelected ? "#ffffff" : color }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected-day agenda */}
      <div className="mx-3 mt-4 bg-white rounded-2xl p-3" data-testid="mobile-cal-agenda">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[13px] font-bold text-[#0B1E3F]">
            {selected.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
          </div>
          <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
            {selectedItems.length} item{selectedItems.length === 1 ? "" : "s"}
          </span>
        </div>
        {loading ? (
          <div className="py-6 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : selectedItems.length === 0 ? (
          <div className="py-6 text-center text-[12.5px] text-slate-400">Nothing scheduled</div>
        ) : (
          <div className="space-y-2">
            {selectedItems.map((it) => {
              if (it.kind === "job") {
                const color = STATUS_COLORS[it.status] || "#94a3b8";
                return (
                  <button
                    key={`job-${it.id}`}
                    onClick={() => navigate(`/mobile/jobs/${it.id}`)}
                    data-testid={`mobile-cal-job-${it.id}`}
                    className="w-full text-left bg-slate-50 rounded-xl p-3 active:bg-slate-100 border border-slate-200"
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-bold uppercase tracking-wide text-slate-500">
                        <Clock className="h-3 w-3" /> {fmtHour(it.start)}
                      </span>
                      <span className="ml-auto text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}22`, color }}>
                        {it.status || "Scheduled"}
                      </span>
                    </div>
                    <div className="mt-1 text-[14px] font-bold text-slate-900 truncate">{it.customer || "—"}</div>
                    {it.address && (
                      <div className="mt-0.5 text-[12px] text-slate-500 inline-flex items-center gap-1 truncate">
                        <MapPin className="h-3 w-3 shrink-0" /> {it.address}
                      </div>
                    )}
                  </button>
                );
              }
              if (it.kind === "task") {
                return (
                  <button
                    key={`task-${it.id}`}
                    onClick={() => setDetail(it)}
                    data-testid={`mobile-cal-task-${it.taskId || it.id}`}
                    className="w-full text-left bg-amber-50 rounded-xl p-3 border border-amber-200 active:opacity-90"
                  >
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 shrink-0 text-amber-600" />
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-bold uppercase tracking-wide text-amber-700">
                        <Clock className="h-3 w-3" /> {it.allDay ? "All day" : fmtHour(it.start)}
                      </span>
                      <span className="ml-auto text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
                        Task
                      </span>
                    </div>
                    <div className="mt-1 text-[14px] font-bold text-amber-900 truncate">{it.title || "Untitled task"}</div>
                    {it.description && <div className="mt-0.5 text-[12px] text-amber-700 line-clamp-2">{it.description}</div>}
                  </button>
                );
              }
              // Event
              const color = it.color || "#3b82f6";
              return (
                <button
                  key={`event-${it.id}`}
                  onClick={() => setDetail(it)}
                  data-testid={`mobile-cal-event-${it.id}`}
                  className="w-full text-left bg-blue-50 rounded-xl p-3 border border-blue-200 active:opacity-90"
                >
                  <div className="flex items-center gap-2">
                    <CalendarClock className="h-4 w-4 shrink-0" style={{ color }} />
                    <span className="inline-flex items-center gap-1 text-[11.5px] font-bold uppercase tracking-wide" style={{ color }}>
                      <Clock className="h-3 w-3" /> {it.allDay ? "All day" : `${fmtHour(it.start)} – ${fmtHour(it.end)}`}
                    </span>
                    <span className="ml-auto text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}22`, color }}>
                      Event
                    </span>
                  </div>
                  <div className="mt-1 text-[14px] font-bold text-slate-900 truncate">{it.title || "Untitled event"}</div>
                  {it.location && (
                    <div className="mt-0.5 text-[12px] text-slate-500 inline-flex items-center gap-1 truncate">
                      <MapPin className="h-3 w-3 shrink-0" /> {it.location}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Dedicated height-based spacer (not padding) so the agenda list clears the
          fixed mobile nav — see MobileJobDetail.jsx/MobileDocDetail.jsx for the same
          pattern; a padding-bottom class here would risk being overridden by
          `.cc-theme .p-4 { padding: ... !important }` in theme-command.css. */}
      <div aria-hidden="true" className="md:hidden shrink-0" style={{ height: "calc(6rem + env(safe-area-inset-bottom))" }} />

      {/* Voice assistant now mounted once globally in App.js, next to MobileNavHost */}

      {createOpen && (
        <MobileCreateModal
          defaultDate={selected}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            refreshAll();
          }}
        />
      )}

      {detail && (
        <CalendarItemDetail
          item={detail}
          onClose={() => setDetail(null)}
          onChanged={() => { setDetail(null); refreshAll(); }}
        />
      )}
    </div>
  );
}


// ---------------- Calendar item detail sheet ----------------

function fmtRange(item) {
  if (!item) return "";
  if (item.allDay) return "All day";
  const s = new Date(item.start);
  const dstr = s.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const tstr = s.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (!item.end || item.kind === "task") return `${dstr} · ${tstr}`;
  const e = new Date(item.end);
  const etstr = e.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${dstr} · ${tstr} – ${etstr}`;
}

function CalendarItemDetail({ item, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isTask = item.kind === "task";
  const isEvent = item.kind === "event";
  const isJob = item.kind === "job";
  const accent = isTask ? "#F59E0B" : isEvent ? (item.color || "#3B82F6") : "#2563EB";
  const chipBg = isTask ? "#FEF3C7" : isEvent ? "#DBEAFE" : "#DBEAFE";
  const kindLabel = isTask ? "Task" : isEvent ? "Event" : "Job";

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isEvent) await calendarApi.removeEvent(item.id);
      else if (isTask) await tasksApi.remove(item.taskId || (item.id || "").replace(/^virt-task-/, ""));
      else return;
      toast.success(`${kindLabel} deleted`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not delete ${kindLabel.toLowerCase()}`);
    } finally { setBusy(false); }
  };

  const completeTask = async () => {
    if (busy || !isTask) return;
    const taskId = item.taskId || (item.id || "").replace(/^virt-task-/, "");
    setBusy(true);
    try {
      await tasksApi.update(taskId, { status: "done" });
      toast.success("Task completed");
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete task");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="mv fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center"
      style={{ backgroundColor: "rgba(15,23,42,0.55)" }}
      onClick={onClose}
      data-testid="mobile-cal-detail"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden"
        style={{ backgroundColor: "#ffffff", color: "#0f172a", maxHeight: "88vh", display: "flex", flexDirection: "column" }}
      >
        <div className="shrink-0 flex items-center gap-2 px-3 py-3" style={{ borderBottom: "1px solid #e2e8f0" }}>
          {isTask ? <CheckSquare className="h-5 w-5" color={accent} style={{ color: accent, stroke: accent }} />
                  : isEvent ? <CalendarClock className="h-5 w-5" color={accent} style={{ color: accent, stroke: accent }} />
                  : <CalIcon className="h-5 w-5" color={accent} style={{ color: accent, stroke: accent }} />}
          <span className="text-[10.5px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: chipBg, color: accent }}>{kindLabel}</span>
          <div className="ml-auto" />
          <button
            onClick={onClose}
            data-testid="mobile-cal-detail-close"
            className="h-9 w-9 rounded-full flex items-center justify-center active:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" color="#475569" style={{ color: "#475569", stroke: "#475569" }} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-3" style={{ backgroundColor: "#ffffff" }}>
          <div className="text-[22px] font-bold leading-tight" style={{ color: "#0f172a" }} data-testid="mobile-cal-detail-title">
            {item.title || (isJob ? (item.customer || "Job") : "Untitled")}
          </div>
          <DetailRow icon={Clock} label="When" value={fmtRange(item)} />
          {isEvent && item.location && <DetailRow icon={MapPin} label="Location" value={item.location} />}
          {isJob && item.address && <DetailRow icon={MapPin} label="Address" value={item.address} />}
          {isJob && item.customer && <DetailRow icon={UserIcon} label="Customer" value={item.customer} />}
          {isJob && item.tech && <DetailRow icon={UserIcon} label="Tech" value={item.tech} />}
          {(item.description || item.notes) && (
            <DetailRow icon={AlignLeft} label={isEvent ? "Description" : "Notes"} value={item.description || item.notes} wrap />
          )}
          {isJob && item.status && <DetailRow icon={Clock} label="Status" value={item.status} />}
          {isEvent && item.calendarName && <DetailRow icon={CalIcon} label="Calendar" value={item.calendarName} />}
        </div>

        <div className="shrink-0 flex items-center gap-2 px-4 py-3" style={{ borderTop: "1px solid #e2e8f0" }}>
          {isTask && (
            <button
              onClick={completeTask}
              disabled={busy}
              data-testid="mobile-cal-detail-complete"
              className="flex-1 h-11 rounded-lg text-[14.5px] font-bold disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              style={{ backgroundColor: "#10B981", color: "#ffffff" }}
            >
              <CheckSquare className="h-4 w-4" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
              Complete
            </button>
          )}
          {(isTask || isEvent) && (
            <button
              onClick={remove}
              disabled={busy}
              data-testid="mobile-cal-detail-delete"
              className="flex-1 h-11 rounded-lg text-[14.5px] font-bold disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" color="#B91C1C" style={{ color: "#B91C1C", stroke: "#B91C1C" }} />
                    : <Trash2 className="h-4 w-4" color="#B91C1C" style={{ color: "#B91C1C", stroke: "#B91C1C" }} />}
              Delete
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="mobile-cal-detail-cancel"
            className="flex-1 h-11 rounded-lg text-[14.5px] font-semibold"
            style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#475569" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, wrap = false }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "#F1F5F9" }}>
        <Icon className="h-4 w-4" color="#334155" style={{ color: "#334155", stroke: "#334155" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11.5px] font-bold uppercase tracking-wide" style={{ color: "#64748b" }}>{label}</div>
        <div className={`text-[15px] mt-0.5 ${wrap ? "whitespace-pre-wrap break-words" : ""}`} style={{ color: "#0f172a" }}>{value}</div>
      </div>
    </div>
  );
}
